import { kioskApi } from '../axios.js';
// This API is used by the frontend to submit orders through our kiosk backend
export const orderApi = {
    /**
     * Submit an order through the kiosk backend
     * The backend will handle forwarding to XOXO API if needed
     * Accepts minimal payload from frontend
     */
    async createOrder(payload) {
        console.log('[DEBUG] orderApi.createOrder called with payload:', JSON.stringify(payload, null, 2));
        if (!payload.targetShopId) {
            const error = new Error('targetShopId is required for order submission');
            console.error('[ERROR] orderApi.createOrder:', error.message);
            throw error;
        }
        try {
            console.log(`[DEBUG] Sending order to: /admin/shops/${payload.targetShopId}/submit-order`);
            const response = await kioskApi.post(`/admin/shops/${payload.targetShopId}/submit-order`, payload);
            console.log('[DEBUG] Order submission response:', response.status, response.data);
            // Calculate brewing time from machine string if available
            const calculateBrewingTime = (machineString) => {
                if (!machineString)
                    return 3000; // Default 3 seconds
                try {
                    const parts = machineString.split(';');
                    const groups = parts.slice(2); // Skip "MC" and "VM"
                    let maxTime = 0;
                    for (const group of groups) {
                        if (!group.trim())
                            continue;
                        const numbers = group.split(',').map(n => parseFloat(n.trim()));
                        if (numbers.length >= 3 && !isNaN(numbers[2])) {
                            maxTime = Math.max(maxTime, numbers[2]);
                        }
                    }
                    return Math.max(maxTime, 0) + 15000; // Add 15 second buffer
                }
                catch (error) {
                    console.error('Error calculating brewing time:', error);
                    return 3000;
                }
            };
            // Store order details in localStorage for approval
            if (response.data?.id) {
                const existingOrders = JSON.parse(localStorage.getItem('pendingOrders') || '[]');
                const brewingTime = calculateBrewingTime(response.data.machineString);
                const newOrder = {
                    id: response.data.id,
                    orderId: response.data.orderId,
                    shopId: payload.targetShopId,
                    recipeId: payload.recipeId, // Store recipe ID for fetching full recipe details
                    name: response.data.name || 'Your Drink',
                    size: response.data.size || 'Medium',
                    displayId: response.data.displayId,
                    glasswareParams: response.data.glasswareParams || {},
                    machineString: response.data.machineString,
                    brewingTime: brewingTime,
                    timestamp: Date.now()
                };
                existingOrders.push(newOrder);
                localStorage.setItem('pendingOrders', JSON.stringify(existingOrders));
                console.log('[DEBUG] Stored order in localStorage:', newOrder);
            }
            return response;
        }
        catch (error) {
            console.error('[ERROR] orderApi.createOrder failed:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    headers: error.config?.headers,
                    data: error.config?.data
                }
            });
            throw error;
        }
    },
    /**
     * Approve an order through the kiosk backend
     * The backend will handle calling the XOXO API
     */
    async approveOrder(orderId, shopId) {
        console.log('[DEBUG] orderApi.approveOrder called with orderId:', orderId, 'shopId:', shopId);
        try {
            const response = await kioskApi.post(`/admin/shops/${shopId}/orders/${orderId}/approve`);
            console.log('[DEBUG] Order approval response:', response.status, response.data);
            return response;
        }
        catch (error) {
            console.error('[ERROR] orderApi.approveOrder failed:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            throw error;
        }
    }
};
// Keep the XOXO API client for backend use only
export const xoxoOrderApi = {
    async createOrder(payload) {
        console.log('[DEBUG] xoxoOrderApi.createOrder called with payload:', JSON.stringify(payload, null, 2));
        try {
            let url = '/order/admin';
            let requestPayload = payload;
            if (payload.status === 'IMMEDIATE') {
                url = '/order/admin/auto-trigger';
                // Strip the status before sending
                const { status, ...strippedPayload } = payload;
                requestPayload = strippedPayload;
            }
            const response = await kioskApi.post(url, requestPayload);
            console.log('[DEBUG] XOXO order submission response:', response.status, response.data);
            return response;
        }
        catch (error) {
            console.error('[ERROR] xoxoOrderApi.createOrder failed:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            throw error;
        }
    },
    async approveOrder(orderId, shopId) {
        console.log('[DEBUG] orderApi.approveOrder called with orderId:', orderId, 'shopId:', shopId);
        try {
            // Use kiosk-server proxy endpoint instead of direct XOXO API
            return kioskApi.post(`/admin/shops/${shopId}/orders/${orderId}/approve`);
        }
        catch (error) {
            console.error('[ERROR] orderApi.approveOrder failed:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            throw error;
        }
    }
};
